import { useTranslation } from "@refinedev/core";

import { Box, Button, Paper, Stack, Typography, TypographyProps } from "@mui/material";

interface EditableCardProps {
  title: string;
  avatar?: React.ReactNode;
  badge?: React.ReactNode;
  onEdit: () => void;
  onSave: () => void;
  onCancel?: () => void;
  children: React.ReactNode;
  isEditing: boolean;
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
  editButtonDisabled?: boolean;
  saveButtonDisabled?: boolean;
  titleVariant?: TypographyProps["variant"];
  extraActions?: React.ReactNode;
  hideEditButton?: boolean;
}

/**
 * A card that displays a title and children, with edit and save buttons.
 * @param title The title of the card.
 * @param avatar Optional avatar element rendered next to the title.
 * @param badge Optional badge element rendered next to the title (e.g. an active/inactive status chip).
 * @param onEdit The function to call when the edit button is clicked.
 * @param onSave The function to call when the save button is clicked.
 * @param onCancel The function to call when the cancel button is clicked.
 * @param children The children to display in the card.
 * @param isEditing The state to determine if the card is in editing mode.
 * @param setIsEditing The function to set the editing state.
 * @param editButtonDisabled Whether the edit button should be disabled.
 * @param titleVariant The variant of the Typography component used for the title.
 * @returns A card with a title, children, and edit/save buttons.
 */
export const EditableCard: React.FC<EditableCardProps> = ({
  title,
  onEdit,
  onSave,
  onCancel,
  children,
  isEditing,
  setIsEditing,
  editButtonDisabled = false,
  saveButtonDisabled = false,
  titleVariant = "h4",
  extraActions,
  hideEditButton = false,
  avatar,
  badge,
}) => {
  const { translate } = useTranslation();

  const handleEditClick = () => {
    if (!isEditing) {
      onEdit();
    }
    // If cancelling (was editing), call onCancel if provided
    else if (onCancel) {
      onCancel();
    } else {
      setIsEditing(false);
    }
  };

  const handleSaveClick = () => {
    onSave();
  };

  return (
    <Paper
      elevation={1}
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
        alignItems: "flex-start",
        width: "100%",
        px: 3,
        py: 2,
        gap: 1.5,
        border: 1,
        borderRadius: 1,
        borderColor: "grey.100",
        alignSelf: "stretch",
      }}
    >
      <Box
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        gap={{ xs: 1.5, md: 0 }}
        alignSelf="stretch"
      >
        <Stack direction="row" spacing={1} alignItems="center">
          {avatar}
          <Typography variant={titleVariant}>{title}</Typography>
          {badge}
        </Stack>
        <Box display="flex" gap={1} alignItems="center">
          {!isEditing && extraActions}
          {(!hideEditButton || isEditing) && (
            <Button
              variant="outlinedRect"
              size="small"
              onClick={handleEditClick}
              disabled={editButtonDisabled ?? false}
            >
              {isEditing ? translate("buttons.cancel") : translate("buttons.edit")}
            </Button>
          )}
          {isEditing && (
            <Button variant="contained" size="small" onClick={handleSaveClick} disabled={saveButtonDisabled ?? false}>
              {translate("buttons.save")}
            </Button>
          )}
        </Box>
      </Box>

      {children}
    </Paper>
  );
};
